import { ReactElement, useMemo, useState } from "react"
import { Box, Divider, MenuItem, Skeleton } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LockedDialog, useLockedDialogState } from "../../../../../../shared/components/dialogs/LockedDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import {
    fetchOrderDetails,
    MeetingOrderAction,
    MeetingServiceQuery,
    OrderDetailsResponse,
} from "../../../../../nonAdminUser/reports/home/meetingServiceApi"
import {
    ActionConfirmationDialog,
    ActionConfirmationDialogMeeting,
} from "../../../../../nonAdminUser/reports/home/components/upcomingOrders/meetings/ActionConfirmationDialog"
import { CreateOrderDialogMode } from "../../../../../../shared/components/createOrder/createOrderTypes"
import { CreateOrderType } from "../../../../../../shared/components/createOrder/helpers"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { ConfirmationState, DialogType } from "../pages/MeetingServiceTab"
import { useOrderDialogUrlState } from "../../../../../../shared/components/createOrder/hooks/useOrderDialogUrlState"
import { ConfigErrorBoundary } from "../../../../../../shared/components/createOrder/components/ConfigErrorBoundary"

const useStyles = makeStyles()(() => ({
    deleteItem: {
        color: tailwindPalette.red[600],
    },
    skeletonCard: {
        height: "1.5rem",
        minWidth: "14rem",
        margin: ".5rem",
        borderRadius: ".625rem",
    },
    noActionsMenuItem: {
        padding: "0.5rem 1rem",
        whiteSpace: "nowrap",
    },
}))

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
type MenuActionKey = keyof MeetingOrderAction | "showReceipt" | "editMeeting"
export type ActionType = "duplicate_meeting_service" | "edit_meeting"

type MenuItemConfig = {
    key?: MenuActionKey
    element: ReactElement
    dividerBefore?: boolean
}

export type AdditionalEventMenuItem = {
    label: string
    key: string
    onClick: (id: number) => void
}

type ProductionOrderMenuContentProps = {
    handleMenuClose: () => void
    row: MeetingServiceOrder
    setShowPackSlipModal: (value: boolean) => void
    openCreditNoteDialog: (orderId: number) => void
    setShowReceiptModal: (value: number) => void
    menuItemClick: () => void
    setConfirmDialogState: (state: ConfirmationState) => void
    onChatOpen: (orderId: number, orderTitle: string) => void
}

export function ProductionOrderMenuContent({
    handleMenuClose,
    row,
    setShowPackSlipModal,
    openCreditNoteDialog,
    setShowReceiptModal,
    menuItemClick,
    setConfirmDialogState,
    onChatOpen,
}: ProductionOrderMenuContentProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()
    const userId = useAppSelector(selectEffectiveUserId)
    const { openDialog } = useOrderDialogUrlState()

    const { lockedDialog, openLockedDialog, closeLockedDialog: closeLockedDialogState } = useLockedDialogState()

    const [confirmationDialog, setConfirmationDialog] = useState<{
        open: boolean
        order: MeetingServiceOrder
        actionType: ActionType
    } | null>(null)

    const { data: orderDetails, isLoading } = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: {
            orderId: row.id,
        },
        select: transformData,
    })

    const orderAction = orderDetails?.orderAction

    const handleShowReceipt = () => setShowReceiptModal(row.id)

    const closeLockedDialog = () => {
        closeLockedDialogState()
        handleMenuClose()
    }

    const duplicateMeetingService = () => {
        setConfirmationDialog({
            open: true,
            order: row,
            actionType: "duplicate_meeting_service",
        })
    }

    const handleDuplicateMeetingServiceConfirm = () => {
        openDialog({
            orderId: row.id,
            mode: CreateOrderDialogMode.DUPLICATE,
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })
    }

    const handleEditMeetingConfirm = () => {
        openDialog({
            orderId: row.id,
            mode: CreateOrderDialogMode.EDIT,
            activeOrderTab: CreateOrderType.MEETING_SERVICE,
        })
    }

    const handleConfirmAction = () => {
        if (!confirmationDialog) return

        switch (confirmationDialog.actionType) {
            case "edit_meeting":
                handleEditMeetingConfirm()
                break

            case "duplicate_meeting_service":
                handleDuplicateMeetingServiceConfirm()
                break
        }

        closeConfirmationDialog()
    }

    const closeConfirmationDialog = () => setConfirmationDialog(null)
    const handleClosePackSlip = () => setShowPackSlipModal(false)

    const renderMenuItems = (orderAction?: MeetingOrderAction) => {
        if (!orderAction) return []
        return menuItemsConfig.reduce<React.ReactNode[]>((acc, { key, element, dividerBefore }, index) => {
            const isShowReceipt = key === "showReceipt"

            if (isShowReceipt || !key) {
                acc.push(element)
                return acc
            }

            const action = orderAction[key as keyof MeetingOrderAction]

            if (key === "editMeeting") {
                if (orderAction.updateBooking || orderAction.updateMeetingService) {
                    if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)
                    acc.push(element)
                }
                return acc
            }

            if (!action) return acc
            if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)

            acc.push(element)
            return acc
        }, [])
    }

    const editMeeting = () => {
        const { hasBooking, hasCatering } = orderDetails?.orderSummary ?? {}

        if (hasBooking && !hasCatering) {
            if (!orderAction?.updateBooking?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateBooking?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateBooking?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-booking-restriction-message"),
                })
            }
        }

        if (hasCatering && !hasBooking) {
            if (!orderAction?.updateMeetingService?.allow) {
                return openLockedDialog({
                    title:
                        orderAction?.updateMeetingService?.message?.title ||
                        l10n.getString("user-home-canteen-upcoming-orders-update"),
                    body:
                        orderAction?.updateMeetingService?.message?.body ||
                        l10n.getString("user-home-canteen-upcoming-orders-update-meeting-restriction-message"),
                })
            }
        }

        setConfirmationDialog({
            open: true,
            order: row,
            actionType: "edit_meeting",
        })
    }

    const noActionsAvailable = [
        <LocalizedStrict
            id="user-home-canteen-news-category-no-actions"
            vars={{
                orderId: row.id.toString(),
            }}
        >
            <Box className={classes.noActionsMenuItem}>No actions available for order #{row.id.toString()}</Box>
        </LocalizedStrict>,
    ]

    const menuItemsConfig: MenuItemConfig[] = [
        {
            element: (
                <LocalizedStrict id="meeting-service-table-production-slip-button">
                    <MenuItem
                        onClick={() => {
                            menuItemClick()
                            setShowPackSlipModal(true)
                        }}
                    >
                        Packing slip
                    </MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            element: (
                <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                    <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                </LocalizedStrict>
            ),
            key: "showReceipt",
        },
        {
            element: (
                <LocalizedStrict id="order-chat-menu-item">
                    <MenuItem
                        onClick={() => {
                            onChatOpen(row.id, row.meetingDetails.meetingName)
                            handleMenuClose()
                        }}
                    >
                        Order message
                    </MenuItem>
                </LocalizedStrict>
            ),
        },
        // hide for now
        // {
        //     element: (
        //         <LocalizedStrict id="meeting-service-table-credit-note-button">
        //             <MenuItem
        //                 onClick={() => {
        //                     menuItemClick()
        //                     openCreditNoteDialog(row.id)
        //                 }}
        //             >
        //                 Credit note
        //             </MenuItem>
        //         </LocalizedStrict>
        //     ),
        // },
        {
            key: "editMeeting",
            element: (
                <LocalizedStrict id="meeting-service-table-update-button">
                    <MenuItem onClick={editMeeting}>Edit</MenuItem>
                </LocalizedStrict>
            ),
            dividerBefore: true,
        },
        {
            key: "duplicateMeetingService",
            element: (
                <LocalizedStrict id="meeting-service-table-duplicate-button">
                    <MenuItem onClick={duplicateMeetingService}>Duplicate</MenuItem>
                </LocalizedStrict>
            ),
        },
        {
            element: (
                <LocalizedStrict id="meeting-service-table-delete-button">
                    <MenuItem
                        className={classes.deleteItem}
                        onClick={() => {
                            setConfirmDialogState({
                                showConfirmDialog: true,
                                items: [row],
                                type: DialogType.DELETE,
                            })
                        }}
                    >
                        Delete
                    </MenuItem>
                </LocalizedStrict>
            ),
            dividerBefore: true,
        },
    ]

    const menuMeeting = useMemo((): ActionConfirmationDialogMeeting | null => {
        if (!confirmationDialog?.order) return null

        const { order } = confirmationDialog

        return {
            id: order.id,
            meetingDetails: {
                meetingName: order.meetingDetails.meetingName,
                startDateTime: order.deliveryDetails.time,
            },
            ...(order?.deliveryDetails && {
                deliveryDetails: {
                    time: order.deliveryDetails.time,
                },
            }),
        }
    }, [confirmationDialog?.order])

    const loadingSkeletons = (
        <>
            <Skeleton variant="rectangular" className={classes.skeletonCard} />
        </>
    )

    const menuItems = isLoading ? loadingSkeletons : orderAction ? renderMenuItems(orderAction) : noActionsAvailable

    return (
        <>
            {menuItems}
            {confirmationDialog && menuMeeting && (
                <ActionConfirmationDialog
                    open={confirmationDialog.open}
                    onClose={closeConfirmationDialog}
                    onConfirm={handleConfirmAction}
                    actionType={confirmationDialog.actionType}
                    meeting={menuMeeting}
                />
            )}
            {lockedDialog.open && <LockedDialog dialogDetails={lockedDialog} onClose={closeLockedDialog} />}
        </>
    )
}

export const ProductionOrderMenu = (props: ProductionOrderMenuContentProps) => (
    <ConfigErrorBoundary onClose={props?.handleMenuClose}>
        <ProductionOrderMenuContent {...props} />
    </ConfigErrorBoundary>
)
